/**
 * ClaimMappingEditor (Sprint C12 Lane 4)
 *
 * Two-column editor for the IdP `claimMapping.claims` map. Each row pairs an
 * IdP-emitted claim name (the source) with an Aaim attribute name (the target).
 *
 * Behavioural contract:
 *   - The component is semi-controlled: rendered rows are derived from
 *     `value` plus an internal "pending" buffer that holds rows still being
 *     edited (one or both fields empty). A row commits to `onChange` once
 *     both `idpClaim` and `aaimAttribute` have non-empty trimmed values.
 *   - Empty rows are visible in the editor but are NOT pushed into `value`,
 *     so the parent never sees a partial draft. A delete on a pending row
 *     drops it without invoking `onChange`.
 *   - Renaming the `idpClaim` of a committed row issues a delete + insert
 *     in the next `onChange` to preserve the parent's record-as-storage
 *     model (keys are claim names).
 *
 * Composes only from `@aaimplatform/ui`. No app-specific styling tokens are
 * hardcoded; classes use the institutional palette aliases (text-primary,
 * border-recessed, etc.).
 *
 * @see schema-registry/identity-provider-registry.openapi.yaml#ClaimMapping
 */

import { useEffect, useState } from 'react';
import { Button, Input, BodyText, TableScroll } from '@aaimplatform/ui';

export interface ClaimMappingEditorProps {
  /** Current map of IdP claim name -> Aaim attribute name. */
  value: Record<string, string>;
  /** Fires whenever a row is added, removed, or edited (committed only). */
  onChange: (next: Record<string, string>) => void;
  /** When true, all interaction is disabled. */
  disabled?: boolean;
  /** Optional id used to associate descriptive text with the editor. */
  id?: string;
}

interface MappingRow {
  /** Stable key used by React for list reconciliation. */
  key: string;
  idpClaim: string;
  aaimAttribute: string;
  /** True once the row has been committed to `value` at least once. */
  committed: boolean;
}

/** Generate a unique synthetic key for a brand-new pending row. */
function newRowKey(): string {
  return `pending-${Math.random().toString(36).slice(2, 9)}`;
}

/** Build the initial row list from the controlled `value` record. */
function rowsFromValue(value: Record<string, string>): MappingRow[] {
  return Object.entries(value).map(([idpClaim, aaimAttribute]) => ({
    key: `committed-${idpClaim}`,
    idpClaim,
    aaimAttribute,
    committed: true,
  }));
}

/**
 * Reduce the row list to a `Record<string,string>` for the controlled
 * `onChange` contract. Only rows whose claim AND attribute are non-empty
 * (post-trim) are included; empty placeholder rows are intentionally
 * dropped so the parent never persists a half-filled mapping.
 */
function rowsToRecord(rows: MappingRow[]): Record<string, string> {
  const record: Record<string, string> = {};
  for (const row of rows) {
    const claim = row.idpClaim.trim();
    const attribute = row.aaimAttribute.trim();
    if (claim && attribute) {
      record[claim] = attribute;
    }
  }
  return record;
}

export function ClaimMappingEditor({
  value,
  onChange,
  disabled = false,
  id,
}: ClaimMappingEditorProps) {
  // Internal row state. Initialized from `value` and re-synced ONLY when
  // the prop value changes externally (e.g. parent reset on modal open).
  const [rows, setRows] = useState<MappingRow[]>(() => rowsFromValue(value));

  // External value changes from outside the editor (e.g. CreateIdpModal
  // hydrating editingProvider on open) should overwrite local state. We
  // rely on stringification rather than ref equality to detect meaningful
  // changes, since the parent passes a fresh object on every render.
  const externalSig = JSON.stringify(value);
  useEffect(() => {
    setRows((current) => {
      const currentRecord = rowsToRecord(current);
      if (JSON.stringify(currentRecord) === externalSig) {
        return current;
      }
      return rowsFromValue(value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSig]);

  /**
   * Commit the next row list to local state AND propagate the resulting
   * record to the parent via `onChange`.
   */
  const updateRows = (nextRows: MappingRow[]) => {
    setRows(nextRows);
    onChange(rowsToRecord(nextRows));
  };

  const handleAddRow = () => {
    if (disabled) return;
    const next: MappingRow[] = [
      ...rows,
      { key: newRowKey(), idpClaim: '', aaimAttribute: '', committed: false },
    ];
    // Adding an empty row does not yet impact `value` -- skip onChange.
    setRows(next);
  };

  const handleDeleteRow = (rowKey: string) => {
    if (disabled) return;
    const next = rows.filter((row) => row.key !== rowKey);
    updateRows(next);
  };

  const handleRowChange = (
    rowKey: string,
    field: 'idpClaim' | 'aaimAttribute',
    nextValue: string,
  ) => {
    if (disabled) return;
    const next = rows.map((row) =>
      row.key === rowKey ? { ...row, [field]: nextValue } : row,
    );
    updateRows(next);
  };

  return (
    <div className="space-y-3" id={id}>
      <BodyText size="sm" color="secondary">
        Map IdP-emitted token claims to Aaim user attributes. Both fields are
        required for a row to persist.
      </BodyText>

      {rows.length === 0 ? (
        <div
          className="border border-dashed border-recessed rounded-sm p-4 text-center"
          data-testid="claim-mapping-empty"
        >
          <BodyText size="sm" color="tertiary">
            No claim mappings configured. Click "Add Mapping" to create one.
          </BodyText>
        </div>
      ) : (
        <div className="border border-recessed rounded-sm overflow-hidden">
          <TableScroll label="Claim mappings">
          <table className="w-full min-w-table-dense text-sm">
            <thead className="bg-warm-50 border-b border-recessed">
              <tr>
                <th
                  scope="col"
                  className="text-left px-3 py-2 font-sans font-medium text-primary"
                >
                  IdP Claim Name
                </th>
                <th
                  scope="col"
                  className="text-left px-3 py-2 font-sans font-medium text-primary"
                >
                  Aaim Attribute Name
                </th>
                <th scope="col" className="w-16 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const claimValid = row.idpClaim.trim().length > 0;
                const attributeValid = row.aaimAttribute.trim().length > 0;
                return (
                  <tr
                    key={row.key}
                    className="border-b border-recessed last:border-b-0"
                  >
                    <td className="px-3 py-2 align-top">
                      <Input
                        value={row.idpClaim}
                        onChange={(e) =>
                          handleRowChange(row.key, 'idpClaim', e.target.value)
                        }
                        placeholder="email"
                        aria-label="IdP claim name"
                        disabled={disabled}
                        className="w-full"
                      />
                      {!claimValid && (
                        <span className="text-xs text-error mt-1 block">
                          Required
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <Input
                        value={row.aaimAttribute}
                        onChange={(e) =>
                          handleRowChange(row.key, 'aaimAttribute', e.target.value)
                        }
                        placeholder="email"
                        aria-label="Aaim attribute name"
                        disabled={disabled}
                        className="w-full"
                      />
                      {!attributeValid && (
                        <span className="text-xs text-error mt-1 block">
                          Required
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRow(row.key)}
                        disabled={disabled}
                        aria-label={`Delete mapping for ${row.idpClaim || 'this claim'}`}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TableScroll>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleAddRow}
          disabled={disabled}
        >
          Add Mapping
        </Button>
      </div>
    </div>
  );
}
